import { useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Workflow from 'models/Workflow';
import { createFacet, workflowQueryKeys } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { Body, BodySmallBold } from 'styles/typography';

import { VALUE_NAMES } from '../constants';

interface Props {
  workflowId: string;
  onClose: () => void;
  // The settled referral, so the caller can show the coverage that was just filed.
  onAdded: (saved: Workflow) => void;
  // The word for the slot the person clicked. Not always the secondary: a packet naming only a
  // secondary leaves the PRIMARY slot empty, and this modal used to say "secondary" either way, so
  // somebody invited to add a primary was then asked for a secondary (OLIO-3141).
  label: string;
  // The slot being filled, and the sequence it stands for. `claim` is undefined for a slot the
  // vocabulary does not name (Coverage 3 and up), which is exactly when there is no sequence to
  // claim and the API should just append.
  position: number;
  claim?: string;
}

// Adding a secondary insurance by hand (OLIO-2794). Takes exactly what a check needs to start — the
// payer's name and the member ID — and records them on a new coverage. Nothing is dispatched from
// here: the API files the coverage and its values, and the engine creates that coverage's payer search
// and verification on its next pass.
export default function AddCoverageModal({ workflowId, onClose, onAdded, label, position, claim }: Props) {
  const [payerName, setPayerName] = useState('');
  const [memberId, setMemberId] = useState('');
  const queryClient = useQueryClient();

  const { mutate: add, isPending } = useMutateWithToast({
    mutationFn: createFacet,
    onSuccess: (saved: Workflow) => {
      // Apply the settled response so the new coverage's tab appears without waiting for the next poll.
      queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.listPrefix });
      onAdded(saved);
      onClose();
    },
  });

  // The payer name is what makes a coverage worth filing — its payer search can't run without one, and
  // a coverage with neither value is the blank one the engine already seeds by itself.
  //
  // The member ID is NOT required here, deliberately. Someone reading an insurance card they only half
  // have should be able to record the payer and let the check ask for the rest: the coverage is filed,
  // its payer search runs, and its verification flags for the member ID on its own tab while the other
  // coverage carries on. Requiring it here would send that person away to find the card first.
  const canSubmit = payerName.trim() !== '';

  const submit = () => {
    if (!canSubmit) return;

    const trimmedMemberId = memberId.trim();

    add({
      workflowId,
      dimension: 'coverage',
      position,
      // The claim on the slot, not decoration. It is what stops a later extraction of the same
      // insurance minting a second coverage on this rank, which the tab strip cannot render (it
      // draws one slot per rank, so the second would have no tab at all).
      ...(claim ? { label: claim } : {}),
      values: {
        [VALUE_NAMES.PAYER_NAME]: payerName.trim(),
        // Omitted rather than sent empty: the API stores '' verbatim and reads it as PRESENT, which
        // would discharge the "needs a member ID" flag and dispatch the check with nothing in it.
        ...(trimmedMemberId ? { [VALUE_NAMES.MEMBER_ID]: trimmedMemberId } : {}),
      },
    });
  };

  return (
    <StandardModal title={`Add ${label} Insurance`} onCancel={onClose} maxWidth='560px'>
      <Form>
        <Body>
          Add the patient’s {label.toLowerCase()} insurance information below. Once submitted, Olio will begin running a
          benefit verification check.
        </Body>

        <SectionTitle>{label} Insurance Information</SectionTitle>

        <Field>
          <FieldLabel>Payer Name</FieldLabel>
          <Input
            value={payerName}
            onChange={(event) => setPayerName(event.target.value)}
            placeholder='Payer Name'
            data-testid='add-coverage-payer-name'
          />
        </Field>

        <Field $narrow>
          <FieldLabel>
            Member ID <Optional>(optional)</Optional>
          </FieldLabel>
          <Input
            value={memberId}
            onChange={(event) => setMemberId(event.target.value)}
            placeholder='Member ID'
            data-testid='add-coverage-member-id'
          />
        </Field>

        <Actions>
          <Button variant='ghost' onClick={onClose} data-testid='add-coverage-cancel'>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || isPending}
            loading={isPending}
            data-testid='add-coverage-submit'>
            Submit
          </Button>
        </Actions>
      </Form>
    </StandardModal>
  );
}

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const Optional = styled.span`
  font-weight: 400;
  color: ${colors.black50};
`;

const SectionTitle = styled(BodySmallBold)`
  display: block;
  color: ${colors.black100};
`;

// Member ID sits at the mock's narrower width; the payer name runs the full row.
const Field = styled.div<{ $narrow?: boolean }>`
  width: ${({ $narrow }) => ($narrow ? '50%' : '100%')};
`;
